import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Box,
  Card,
  Chip,
  Tab,
  Tabs,
  Table,
  Stack,
  Modal,
  Button,
  Divider,
  TextField,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Tooltip,
  CircularProgress,
  Avatar,
  InputAdornment,
  Select,
  MenuItem,
} from '@mui/material';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import Iconify from '../../components/Iconify';
import SearchNotFound from '../../components/SearchNotFound';
import { ListHead } from '../../sections/@dashboard/table';
import Loader from '../../components/Loader';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'userFullName', label: 'User Name', alignRight: false },
  { id: 'userId', label: 'User ID', alignRight: false },
  { id: 'userEmail', label: 'Email', alignRight: false },
  { id: 'city', label: 'City', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'uploadedAt', label: 'Uploaded At', alignRight: false },
  { id: 'actions', label: 'Actions', alignRight: false },
];

const SSN_TABLE_HEAD = [
  { id: 'userName',    label: 'User Name',    alignRight: false },
  { id: 'userId',     label: 'User ID',      alignRight: false },
  { id: 'email',      label: 'Email',        alignRight: false },
  { id: 'currentSsn', label: 'Current SSN',  alignRight: false },
  { id: 'newSsn',     label: 'New SSN',      alignRight: false },
  { id: 'status',     label: 'Status',       alignRight: false },
  { id: 'requestedAt', label: 'Requested At', alignRight: false },
  { id: 'actions',    label: 'Actions',      alignRight: false },
];

const ACTIVITY_TABLE_HEAD = [
  { id: 'targetUser',   label: 'Target User',    alignRight: false },
  { id: 'action',      label: 'Action',          alignRight: false },
  { id: 'comments',    label: 'Comments',        alignRight: false },
  { id: 'performedBy', label: 'Performed By',    alignRight: false },
  { id: 'performedAt', label: 'Performed At',    alignRight: false },
];

const ROW_AGE_CONFIG = [
  { maxHours: 6,  bg: '#e8f5e9', label: '< 6 hours',   description: 'Recently uploaded — within 6 hours' },
  { maxHours: 12, bg: '#fff8e1', label: '6 – 12 hours', description: 'Waiting — uploaded 6 to 12 hours ago' },
  { maxHours: 24, bg: '#fff3e0', label: '12 – 24 hours',description: 'Overdue — uploaded 12 to 24 hours ago' },
  { maxHours: Infinity, bg: '#ffebee', label: '> 24 hours', description: 'Critical — uploaded more than 24 hours ago' },
];

function getRowAgeBg(uploadedAt) {
  if (!uploadedAt) return ROW_AGE_CONFIG[0].bg;
  const hoursAgo = (Date.now() - new Date(uploadedAt).getTime()) / (1000 * 60 * 60);
  const cfg = ROW_AGE_CONFIG.find((c) => hoursAgo < c.maxHours);
  return cfg ? cfg.bg : ROW_AGE_CONFIG[ROW_AGE_CONFIG.length - 1].bg;
}

const STATUS_CONFIG = {
  0: { label: 'Pending', color: 'warning' },
  1: { label: 'Approved', color: 'success' },
  2: { label: 'Rejected', color: 'error' },
  3: { label: 'Partial Rejected', color: 'warning' },
};

const TABS = [
  { label: 'Pending',         sublabel: 'Awaiting review',     status: 0,    icon: 'eva:clock-outline',              color: '#f59e0b', bg: '#fffbeb' },
  { label: 'Rejected',        sublabel: 'Documents declined',  status: 2,    icon: 'eva:close-circle-outline',       color: '#ef4444', bg: '#fef2f2' },
  { label: 'Partial Rejected',sublabel: 'Some docs declined',  status: 3,    icon: 'eva:alert-triangle-outline',     color: '#f97316', bg: '#fff7ed' },
  { label: 'Accepted',        sublabel: 'Fully verified',      status: 1,    icon: 'eva:checkmark-circle-2-outline', color: '#10b981', bg: '#f0fdf4' },
  { label: 'All',             sublabel: 'Every request',       status: null, icon: 'eva:layers-outline',             color: '#6366f1', bg: '#eef2ff' },
  { label: 'SSN Requests',    sublabel: 'SSN update requests', isSsnTab: true,      status: null, icon: 'eva:person-done-outline', color: '#0ea5e9', bg: '#f0f9ff' },
  { label: 'Admin Activity',  sublabel: 'Admin action history',isActivityTab: true, status: null, icon: 'eva:activity-outline',    color: '#8b5cf6', bg: '#faf5ff' },
];

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '92%',
  maxWidth: 1120,
  height: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 3,
  boxShadow: '0 32px 80px rgba(0,0,0,0.22)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const SEARCH_COLUMN_OPTIONS = [
  { value: '',  label: 'All fields' },
  { value: '1', label: 'First Name' },
  { value: '2', label: 'Last Name' },
  { value: '3', label: 'Full Name' },
  { value: '4', label: 'Email' },
  { value: '5', label: 'SSN' },
  { value: '6', label: 'User ID' },
];

const SSN_SEARCH_COLUMN_OPTIONS = [
  { value: '',  label: 'All fields' },
  { value: '1', label: 'First Name' },
  { value: '2', label: 'Last Name' },
  { value: '3', label: 'Full Name' },
  { value: '4', label: 'Email' },
  { value: '5', label: 'Current SSN' },
  { value: '6', label: 'New SSN' },
  { value: '7', label: 'User ID' },
];

// ----------------------------------------------------------------------

export default function UserVerification() {
  const location = useLocation();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('userFullName');

  // preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [docList, setDocList] = useState([]);
  const [activeDocTab, setActiveDocTab] = useState(0);
  const [previewName, setPreviewName] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [activeRow, setActiveRow] = useState(null);

  // reject modal state
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectTargetDocId, setRejectTargetDocId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // profile panel state
  const [profilePanelOpen, setProfilePanelOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // SSN verification request state
  const [ssnRecords, setSsnRecords] = useState([]);
  const [ssnLoading, setSsnLoading] = useState(false);
  const [ssnTotalCount, setSsnTotalCount] = useState(0);

  // Activity logs state
  const [activityRecords, setActivityRecords] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityTotalCount, setActivityTotalCount] = useState(0);

  // Search state — per-tab (each tab keeps its own history independently)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchColumn, setSearchColumn] = useState('');
  const [tabSearchStates, setTabSearchStates] = useState(
    () => TABS.map(() => ({ applied: '', history: [''], idx: 0 }))
  );
  const currTabSearch = tabSearchStates[activeTab] ?? { applied: '', history: [''], idx: 0 };
  const appliedSearch = currTabSearch.applied;
  const searchHistory = currTabSearch.history;
  const historyIdx    = currTabSearch.idx;

  const updateTabSearch = (patch) =>
    setTabSearchStates((prev) =>
      prev.map((s, i) => (i === activeTab ? { ...s, ...patch } : s))
    );

  // Highlight state — row to highlight from notificationVal URL param
  const [highlightId, setHighlightId] = useState(null);

  // On mount: if ?notificationVal is present, handle routing based on type param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nVal = params.get('notificationVal');
    if (!nVal) return;
    const type = params.get('type');

    if (type === 'ssn') {
      // SSN Update Request notification → switch to SSN Requests tab (index 5) and highlight row
      setHighlightId(String(nVal));
      setActiveTab(5);
      setPage(0);
      return;
    }

    const requestId = String(nVal);
    setHighlightId(requestId); // stored requestId — used for row highlight match
    setActiveTab(0);           // switch to Pending tab
    setPage(0);

    const applySearch = (q) => {
      setSearchQuery(q);
      setTabSearchStates((prev) =>
        prev.map((s, i) =>
          i === 0 ? { applied: q, history: ['', q], idx: 1 } : s
        )
      );
    };

    // Resolve requestId → userId, then search by userId so the record appears.
    // Also attempt to match by userId (notificationVal may be userId for some notification types).
    applySearch(requestId); // immediate search so records load even if API call below is slow
    axios
      .get(`/admin/bankid-verification/request/${requestId}/download-urls`)
      .then((res) => {
        const userId =
          res.data?.user?.userId ??
          res.data?.userId ??
          res.data?.response?.user?.userId ??
          null;
        if (userId) applySearch(String(userId));
      })
      .catch(() => {}); // search already applied above as fallback
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SSN detail modal state
  const [ssnDetailOpen, setSsnDetailOpen] = useState(false);
  const [ssnDetailData, setSsnDetailData] = useState(null);
  const [ssnDetailLoading, setSsnDetailLoading] = useState(false);
  const [ssnActiveRow, setSsnActiveRow] = useState(null);
  const [ssnConfirmOpen, setSsnConfirmOpen] = useState(false);
  const [ssnConfirmType, setSsnConfirmType] = useState(null);
  const [ssnActionLoading, setSsnActionLoading] = useState(false);
  const [ssnRejectReason, setSsnRejectReason] = useState('');

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      const tabStatus = TABS[activeTab].status;
      const statusParam = tabStatus !== null ? `&status=${tabStatus}` : '';
      const searchParam = appliedSearch ? `&searchQuery=${encodeURIComponent(appliedSearch)}` : '';
      const columnParam = searchColumn ? `&SearchColumn=${searchColumn}` : '';
      const res = await axios.get(
        `/admin/bankid-verification?PageSize=${rowsPerPage}&PageNumber=${page + 1}${statusParam}${searchParam}${columnParam}`
      );
      if (res?.status === 200) {
        setRecords(res.data.response ?? []);
        setTotalCount(res.data.totalCount ?? 0);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to fetch records');
    } finally {
      setLoading(false);
    }
  };

  const fetchSsnRequests = async () => {
    try {
      setSsnLoading(true);
      const searchParam = appliedSearch ? `&searchQuery=${encodeURIComponent(appliedSearch)}` : '';
      const columnParam = searchColumn ? `&SearchColumn=${searchColumn}` : '';
      const res = await axios.get(
        `/admin/ssn-update-request?PageSize=${rowsPerPage}&PageNumber=${page + 1}${searchParam}${columnParam}`
      );
      if (res?.status === 200) {
        setSsnRecords(res.data.response ?? []);
        setSsnTotalCount(res.data.totalCount ?? 0);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to fetch SSN requests');
    } finally {
      setSsnLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      setActivityLoading(true);
      const searchParam = appliedSearch ? `&searchQuery=${encodeURIComponent(appliedSearch)}` : '';
      const columnParam = searchColumn ? `&SearchColumn=${searchColumn}` : '';
      const res = await axios.get(
        `/admin/bankid-verification/activity-logs?PageSize=${rowsPerPage}&PageNumber=${page + 1}${searchParam}${columnParam}`
      );
      if (res?.status === 200) {
        setActivityRecords(res.data.response ?? []);
        setActivityTotalCount(res.data.totalCount ?? 0);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to fetch activity logs');
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    if (TABS[activeTab]?.isActivityTab) {
      fetchActivityLogs();
    } else if (TABS[activeTab]?.isSsnTab) {
      fetchSsnRequests();
    } else {
      fetchVerifications();
    }
  }, [page, rowsPerPage, activeTab, tabSearchStates, searchColumn]);

  const handleSsnView = async (row) => {
    const requestId = row.requestId ?? row.id;
    setSsnActiveRow(row);
    setSsnDetailData(null);
    setSsnDetailLoading(true);
    setSsnDetailOpen(true);
    try {
      const res = await axios.get(`/admin/ssn-update-request/${requestId}`);
      setSsnDetailData(res.data?.response ?? res.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load SSN request');
      setSsnDetailOpen(false);
    } finally {
      setSsnDetailLoading(false);
    }
  };

  const handleCloseSsnDetail = () => {
    setSsnDetailOpen(false);
    setSsnDetailData(null);
    setSsnActiveRow(null);
  };

  const handleSsnConfirm = async () => {
    const requestId = ssnActiveRow?.requestId ?? ssnActiveRow?.id;
    if (!requestId) return;
    try {
      setSsnActionLoading(true);
      if (ssnConfirmType === 'approve') {
        await axios.put(`/admin/ssn-update-request/${requestId}/approve`);
        toast.success('SSN request approved successfully');
      } else {
        await axios.put(`/admin/ssn-update-request/${requestId}/reject`, { reason: ssnRejectReason });
        toast.success('SSN request rejected successfully');
      }
      setSsnConfirmOpen(false);
      setSsnRejectReason('');
      setSsnDetailOpen(false);
      setSsnDetailData(null);
      setSsnActiveRow(null);
      fetchSsnRequests();
    } catch (err) {
      toast.error(err?.response?.data?.message || `Failed to ${ssnConfirmType} request`);
    } finally {
      setSsnActionLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
    // Restore the search field to the new tab's last applied search
    setSearchQuery(tabSearchStates[newValue]?.applied ?? '');
  };

  const handleSearchSubmit = () => {
    const q = searchQuery.trim();
    if (q === appliedSearch) return;
    const newHistory = [...searchHistory.slice(0, historyIdx + 1), q];
    updateTabSearch({ applied: q, history: newHistory, idx: newHistory.length - 1 });
    setPage(0);
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    if (appliedSearch === '') return;
    const newHistory = [...searchHistory.slice(0, historyIdx + 1), ''];
    updateTabSearch({ applied: '', history: newHistory, idx: newHistory.length - 1 });
    setPage(0);
  };

  const handleUndo = () => {
    const newIdx = historyIdx - 1;
    const q = searchHistory[newIdx];
    setSearchQuery(q);
    updateTabSearch({ applied: q, idx: newIdx });
    setPage(0);
  };

  const handleRedo = () => {
    const newIdx = historyIdx + 1;
    const q = searchHistory[newIdx];
    setSearchQuery(q);
    updateTabSearch({ applied: q, idx: newIdx });
    setPage(0);
  };

  const handleClearAll = () => {
    setSearchQuery('');
    setSearchColumn('');
    if (appliedSearch !== '') {
      const newHistory = [...searchHistory.slice(0, historyIdx + 1), ''];
      updateTabSearch({ applied: '', history: newHistory, idx: newHistory.length - 1 });
      setPage(0);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownload = async (row) => {
    try {
      setPreviewLoading(true);
      const fullName = row.user?.fullName?.trim() || `${row.user?.firstName || ''} ${row.user?.lastName || ''}`.trim();
      setPreviewName(fullName || `User ${row.user?.userId}`);
      setActiveRow(row);
      setDocList([]);
      setActiveDocTab(0);
      setPreviewOpen(true);

      const requestId = row.user?.requestId ?? row.requestId;
      const res = await axios.get(`/admin/bankid-verification/request/${requestId}/download-urls`);
      // API returns { user, documents } or { response } or plain array
      const list = Array.isArray(res.data)
        ? res.data
        : (res.data?.documents ?? res.data?.response ?? []);
      if (!list.length) throw new Error('No documents returned');
      setDocList(list);
      // Also update activeRow with richer user data from this response
      if (res.data?.user) {
        setActiveRow((prev) => ({ ...prev, user: res.data.user }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load document');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    const shouldRefresh = needsRefresh;
    setPreviewOpen(false);
    setDocList([]);
    setActiveDocTab(0);
    setPreviewName('');
    setActiveRow(null);
    setNeedsRefresh(false);
    setProfilePanelOpen(false);
    setProfileData(null);
    if (shouldRefresh) fetchVerifications();
  };

  const handleAccept = async (documentId) => {
    if (!documentId) return;
    try {
      setActionLoading(true);
      await axios.put(`/admin/bankid-verification/document/${documentId}/approve`);
      toast.success('Document approved successfully');
      setDocList((prev) => prev.map((d) => d.documentId === documentId ? { ...d, status: 1 } : d));
      setNeedsRefresh(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to approve document');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenReject = (documentId) => {
    setRejectReason('');
    setRejectTargetDocId(documentId);
    setRejectOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectTargetDocId) return;
    try {
      setActionLoading(true);
      await axios.put(`/admin/bankid-verification/document/${rejectTargetDocId}/reject`, { reason: rejectReason });
      toast.success('Document rejected successfully');
      setDocList((prev) => prev.map((d) => d.documentId === rejectTargetDocId ? { ...d, status: 2, rejectionReason: rejectReason } : d));
      setNeedsRefresh(true);
      setRejectOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to reject document');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleProfile = async () => {
    if (profilePanelOpen) {
      setProfilePanelOpen(false);
      return;
    }
    const userId = activeRow?.user?.userId;
    if (!userId) return;
    setProfileLoading(true);
    setProfilePanelOpen(true);
    try {
      const res = await axios.get(`/admin/bankid-verification/user/${userId}`);
      const raw = res.data?.response?.[0] ?? (Array.isArray(res.data) ? res.data[0] : res.data);
      setProfileData(raw?.user ?? null);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load profile');
      setProfilePanelOpen(false);
    } finally {
      setProfileLoading(false);
    }
  };

  const isNotFound = !loading && records.length === 0;

  return (
    <Page title="User Verification">
      <Container maxWidth="xl">
        {/* ── Page Header ── */}
        <Box sx={{
          mb: 3,
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 40%, #1976d2 70%, #42a5f5 100%)',
          boxShadow: '0 8px 32px rgba(13,71,161,0.28)',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Decorative circles */}
          <Box sx={{ position: 'absolute', top: -40, right: -40, width: 180, height: 180, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
          <Box sx={{ position: 'absolute', bottom: -30, right: 120, width: 110, height: 110, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{
                p: 1.5,
                borderRadius: 2.5,
                bgcolor: 'rgba(255,255,255,0.15)',
                border: '1.5px solid rgba(255,255,255,0.25)',
                display: 'flex',
                backdropFilter: 'blur(8px)',
              }}>
                <Iconify icon="eva:shield-outline" width={28} height={28} sx={{ color: '#fff' }} />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={800} color="#fff" sx={{ lineHeight: 1.2, letterSpacing: '-0.5px' }}>
                  User Verification
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.4 }}>
                  Review BankID documents · Manage SSN updates · Track admin actions
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1.5}>
              {[
                { icon: 'eva:clock-fill',             label: 'Pending',   color: '#fbbf24' },
                { icon: 'eva:checkmark-circle-2-fill', label: 'Accepted',  color: '#34d399' },
                { icon: 'eva:close-circle-fill',      label: 'Rejected',  color: '#f87171' },
              ].map((s) => (
                <Box key={s.label} sx={{
                  display: 'flex', alignItems: 'center', gap: 0.7,
                  px: 1.4, py: 0.7,
                  borderRadius: 6,
                  bgcolor: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.18)',
                }}>
                  <Iconify icon={s.icon} width={14} height={14} sx={{ color: s.color }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, fontSize: 11 }}>{s.label}</Typography>
                </Box>
              ))}
            </Stack>
          </Stack>
        </Box>

        {/* ── Tabs ── */}
        <Box sx={{
          mb: 2.5,
          bgcolor: 'background.paper',
          borderRadius: 2.5,
          boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
          border: '1.5px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 1,
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                background: `linear-gradient(90deg, ${TABS[activeTab]?.color ?? '#1976d2'}, ${TABS[activeTab]?.color ?? '#42a5f5'}88)`,
              },
              '& .MuiTab-root': {
                minHeight: 64,
                fontWeight: 600,
                fontSize: 13,
                textTransform: 'none',
                color: 'text.secondary',
                borderRadius: '10px 10px 0 0',
                mr: 0.5,
                px: 2,
                transition: 'all 0.2s',
              },
              '& .Mui-selected': {
                color: TABS[activeTab]?.color ?? 'primary.main',
                bgcolor: `${TABS[activeTab]?.bg ?? '#eef2ff'}`,
              },
            }}
          >
            {TABS.map((tab) => (
              <Tooltip
                key={tab.label}
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="caption" fontWeight={700} display="block">{tab.label}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>{tab.sublabel}</Typography>
                  </Box>
                }
                arrow
                placement="bottom"
              >
                <Tab
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.8}>
                      <Box sx={{
                        p: 0.55,
                        borderRadius: 1.2,
                        bgcolor: activeTab === TABS.indexOf(tab) ? `${tab.color}22` : 'grey.100',
                        display: 'flex',
                        transition: 'all 0.2s',
                      }}>
                        <Iconify icon={tab.icon} width={16} height={16} sx={{ color: activeTab === TABS.indexOf(tab) ? tab.color : 'text.disabled' }} />
                      </Box>
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="caption" fontWeight={700} display="block" lineHeight={1.2} sx={{ color: 'inherit', fontSize: 12 }}>
                          {tab.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10, fontWeight: 500, lineHeight: 1 }}>
                          {tab.sublabel}
                        </Typography>
                      </Box>
                    </Stack>
                  }
                  sx={{ minHeight: 64 }}
                />
              </Tooltip>
            ))}
          </Tabs>
        </Box>

        {/* ── Search Bar ── */}
        <Box sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          p: 1.5,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1.5px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        }}>
          {(() => {
            const isSsn = TABS[activeTab]?.isSsnTab;
            const opts = isSsn ? SSN_SEARCH_COLUMN_OPTIONS : SEARCH_COLUMN_OPTIONS;
            return (
              <Select
                size="small"
                value={searchColumn}
                onChange={(e) => setSearchColumn(e.target.value)}
                displayEmpty
                sx={{
                  minWidth: 140,
                  borderRadius: 1.5,
                  bgcolor: '#f8fafc',
                  fontWeight: 600,
                  fontSize: 13,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.300' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.light' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                }}
              >
                {opts.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            );
          })()}
          <TextField
            size="small"
            placeholder="Search…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
            sx={{ flex: 1, maxWidth: 420 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" width={18} height={18} sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <Tooltip title="Clear search" arrow>
                    <IconButton size="small" onClick={handleSearchClear} edge="end">
                      <Iconify icon="eva:close-fill" width={16} height={16} />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : null,
              sx: { borderRadius: 1.5, bgcolor: '#f8fafc' },
            }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSearchSubmit}
            startIcon={<Iconify icon="eva:search-fill" width={16} height={16} />}
            sx={{
              borderRadius: 1.5,
              px: 2.5,
              fontWeight: 700,
              background: 'linear-gradient(135deg,#1976d2,#42a5f5)',
              boxShadow: '0 2px 8px rgba(25,118,210,0.3)',
              '&:hover': { background: 'linear-gradient(135deg,#1565c0,#1976d2)' },
            }}
          >
            Search
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
            <Tooltip title={historyIdx > 0 ? `Undo — restore "${searchHistory[historyIdx - 1] || 'empty'}"`  : 'Nothing to undo'} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={handleUndo}
                  disabled={historyIdx <= 0}
                  sx={{
                    border: '1.5px solid',
                    borderColor: historyIdx > 0 ? 'primary.light' : 'grey.300',
                    bgcolor: historyIdx > 0 ? 'primary.lighter' : 'transparent',
                    color: historyIdx > 0 ? 'primary.dark' : 'text.disabled',
                    transition: 'all 0.18s',
                    '&:hover:not(:disabled)': { bgcolor: 'primary.light', color: '#fff' },
                  }}
                >
                  <Iconify icon="eva:corner-up-left-fill" width={16} height={16} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={historyIdx < searchHistory.length - 1 ? `Redo — restore "${searchHistory[historyIdx + 1] || 'empty'}"` : 'Nothing to redo'} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={handleRedo}
                  disabled={historyIdx >= searchHistory.length - 1}
                  sx={{
                    border: '1.5px solid',
                    borderColor: historyIdx < searchHistory.length - 1 ? 'primary.light' : 'grey.300',
                    bgcolor: historyIdx < searchHistory.length - 1 ? 'primary.lighter' : 'transparent',
                    color: historyIdx < searchHistory.length - 1 ? 'primary.dark' : 'text.disabled',
                    transition: 'all 0.18s',
                    '&:hover:not(:disabled)': { bgcolor: 'primary.light', color: '#fff' },
                  }}
                >
                  <Iconify icon="eva:corner-up-right-fill" width={16} height={16} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {appliedSearch && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, ml: 1 }}>
              <Typography variant="caption" color="text.secondary">Results for:</Typography>
              <Chip
                label={appliedSearch}
                size="small"
                onDelete={handleSearchClear}
                sx={{ fontWeight: 700, bgcolor: 'primary.lighter', color: 'primary.dark', border: '1px solid', borderColor: 'primary.light' }}
              />
            </Box>
          )}
          {(appliedSearch || searchColumn) && (
            <Tooltip title="Clear all filters" arrow>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={handleClearAll}
                startIcon={<Iconify icon="eva:trash-2-outline" width={15} height={15} />}
                sx={{
                  ml: 'auto',
                  borderRadius: 1.5,
                  fontWeight: 700,
                  fontSize: 12,
                  px: 1.8,
                  borderWidth: 1.5,
                  '&:hover': { borderWidth: 1.5, bgcolor: 'error.lighter' },
                }}
              >
                Clear All
              </Button>
            </Tooltip>
          )}
        </Box>

        <Card sx={{ borderRadius: 2.5, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1.5px solid', borderColor: 'divider', overflow: 'hidden' }}>
          {/* Table context bar */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2.5, py: 1.4,
            borderBottom: '1.5px solid', borderColor: 'divider',
            background: `linear-gradient(90deg, ${TABS[activeTab]?.bg ?? '#f8fafc'} 0%, #fff 65%)`,
          }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ p: 0.65, borderRadius: 1.2, bgcolor: `${TABS[activeTab]?.color ?? '#1976d2'}1a`, display: 'flex', flexShrink: 0 }}>
                <Iconify icon={TABS[activeTab]?.icon ?? 'eva:layers-outline'} width={16} height={16} sx={{ color: TABS[activeTab]?.color ?? '#1976d2' }} />
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.15, fontSize: 13, color: 'text.primary' }}>{TABS[activeTab]?.label}</Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>{TABS[activeTab]?.sublabel}</Typography>
              </Box>
            </Stack>
            <Chip
              label={`${TABS[activeTab]?.isActivityTab ? activityTotalCount : TABS[activeTab]?.isSsnTab ? ssnTotalCount : totalCount} records`}
              size="small"
              sx={{
                fontWeight: 700, fontSize: 11, height: 22,
                bgcolor: `${TABS[activeTab]?.color ?? '#1976d2'}15`,
                color: TABS[activeTab]?.color ?? '#1976d2',
                border: '1px solid', borderColor: `${TABS[activeTab]?.color ?? '#1976d2'}30`,
              }}
            />
          </Box>
          {(TABS[activeTab]?.isActivityTab ? activityLoading : TABS[activeTab]?.isSsnTab ? ssnLoading : loading) ? (
            <Box py={6}>
              <Loader />
            </Box>
          ) : TABS[activeTab]?.isActivityTab ? (
            /* ── Admin BankID Activity Logs Table ── */
            <>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 900 }}>
                  <Table>
                    <ListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={ACTIVITY_TABLE_HEAD}
                      rowCount={activityRecords.length}
                      numSelected={0}
                      onRequestSort={handleRequestSort}
                      hideCheckboxColumnHeader
                    />
                    <TableBody>
                      {activityRecords.map((row, idx) => {
                        const target = row.targetUser ?? {};
                        const admin  = row.performedByAdmin ?? {};
                        const targetName = target.fullName?.trim() || `${target.firstName || ''} ${target.lastName || ''}`.trim() || '—';
                        const targetInitials = targetName !== '—'
                          ? targetName.split(' ').filter(Boolean).map((n) => n[0]).join('').toUpperCase().slice(0, 2)
                          : '?';
                        const adminInitials = (admin.name || '')
                          .split(' ').filter(Boolean).map((n) => n[0]).join('').toUpperCase().slice(0, 2) || 'A';
                        const performedAt = row.performedAt ?? null;
                        const actionLower = (row.action || '').toLowerCase();
                        const actionStyle = actionLower.includes('reject') || actionLower.includes('unverif')
                          ? { bg: '#fef2f2', text: '#b91c1c', border: '#fca5a5', icon: 'eva:close-circle-fill' }
                          : actionLower.includes('approv') || (actionLower.includes('verif') && !actionLower.includes('unverif'))
                          ? { bg: '#f0fdf4', text: '#15803d', border: '#86efac', icon: 'eva:checkmark-circle-2-fill' }
                          : actionLower.includes('updat')
                          ? { bg: '#eff6ff', text: '#1d4ed8', border: '#93c5fd', icon: 'eva:edit-2-fill' }
                          : { bg: '#f8fafc', text: '#475569', border: '#cbd5e1', icon: 'eva:info-fill' };
                        return (
                          <TableRow
                            hover
                            key={row.id ?? idx}
                            tabIndex={-1}
                            sx={{
                              '&:last-child td': { border: 0 },
                              bgcolor: idx % 2 !== 0 ? 'rgba(248,250,252,0.7)' : 'transparent',
                              transition: 'background 0.15s, box-shadow 0.15s',
                              '&:hover': { bgcolor: '#f0f9ff !important', boxShadow: 'inset 3px 0 0 #0ea5e9' },
                            }}
                          >
                            {/* Target User */}
                            <TableCell sx={{ py: 1.5 }}>
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Avatar sx={{
                                  width: 36, height: 36, fontSize: 13, fontWeight: 800, flexShrink: 0,
                                  background: 'linear-gradient(135deg, #0ea5e9, #38bdf8)',
                                  color: '#fff', boxShadow: '0 2px 8px rgba(14,165,233,0.3)',
                                  border: '2px solid rgba(255,255,255,0.8)',
                                }}>
                                  {targetInitials}
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: 13, lineHeight: 1.3 }}>{targetName}</Typography>
                                  <Typography variant="caption" color="text.disabled" noWrap sx={{ fontSize: 11 }}>{target.email || '—'}</Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            {/* Action */}
                            <TableCell sx={{ py: 1.5 }}>
                              <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.6,
                                px: 1.2, py: 0.5, borderRadius: 6,
                                bgcolor: actionStyle.bg, border: '1.5px solid', borderColor: actionStyle.border,
                              }}>
                                <Iconify icon={actionStyle.icon} width={13} height={13} sx={{ color: actionStyle.text, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: actionStyle.text, lineHeight: 1, whiteSpace: 'nowrap' }}>
                                  {row.action || '—'}
                                </Typography>
                              </Box>
                            </TableCell>
                            {/* Comments */}
                            <TableCell sx={{ maxWidth: 240, py: 1.5 }}>
                              <Tooltip title={row.comments || ''} arrow placement="top">
                                <Typography variant="body2" noWrap sx={{ maxWidth: 230, cursor: 'default', fontSize: 12, color: row.comments ? 'text.secondary' : 'text.disabled', fontStyle: row.comments ? 'normal' : 'italic' }}>
                                  {row.comments || 'No comment'}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            {/* Performed By */}
                            <TableCell sx={{ py: 1.5 }}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Avatar sx={{
                                  width: 30, height: 30, fontSize: 11, fontWeight: 800, flexShrink: 0,
                                  background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
                                  color: '#fff', boxShadow: '0 2px 6px rgba(245,158,11,0.3)',
                                }}>
                                  {adminInitials}
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: 12, lineHeight: 1.3 }}>{admin.name || '—'}</Typography>
                                  {admin.email && <Typography variant="caption" color="text.disabled" noWrap sx={{ fontSize: 10 }}>{admin.email}</Typography>}
                                </Box>
                              </Stack>
                            </TableCell>
                            {/* Performed At */}
                            <TableCell sx={{ py: 1.5 }}>
                              <Tooltip title={performedAt ? new Date(performedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''} arrow>
                                <Box>
                                  {performedAt ? (
                                    <>
                                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: 12, lineHeight: 1.3, color: 'text.primary' }}>
                                        {(() => { const diff = Date.now() - new Date(performedAt).getTime(); const h = diff / 3600000; const d = Math.floor(h / 24); return h < 1 ? `${Math.floor(diff / 60000)}m ago` : h < 24 ? `${Math.floor(h)}h ago` : d < 7 ? `${d}d ago` : new Date(performedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); })()}
                                      </Typography>
                                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                                        {new Date(performedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                      </Typography>
                                    </>
                                  ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                                </Box>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    {!activityLoading && activityRecords.length === 0 && (
                      <TableBody>
                        <TableRow>
                          <TableCell align="center" colSpan={ACTIVITY_TABLE_HEAD.length} sx={{ py: 0, border: 0 }}>
                            <Box sx={{
                              py: 8, px: 4,
                              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
                              background: 'linear-gradient(180deg, #faf5ff 0%, #fff 100%)',
                            }}>
                              <Box sx={{
                                position: 'relative', mb: 1,
                                '@keyframes floatIcon': {
                                  '0%,100%': { transform: 'translateY(0px)' },
                                  '50%': { transform: 'translateY(-8px)' },
                                },
                              }}>
                                <Box sx={{
                                  width: 72, height: 72, borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  boxShadow: '0 8px 28px rgba(139,92,246,0.18)',
                                  animation: 'floatIcon 3s ease-in-out infinite',
                                }}>
                                  <Iconify icon="eva:activity-outline" width={34} height={34} sx={{ color: '#7c3aed' }} />
                                </Box>
                                <Box sx={{
                                  position: 'absolute', bottom: 2, right: -2,
                                  width: 24, height: 24, borderRadius: '50%',
                                  bgcolor: '#fff', border: '2px solid #ede9fe',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                }}>
                                  <Iconify icon="eva:search-outline" width={12} height={12} sx={{ color: '#7c3aed' }} />
                                </Box>
                              </Box>
                              <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#3b0764', fontSize: 15 }}>
                                No Activity Logs Found
                              </Typography>
                              <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 300, textAlign: 'center', lineHeight: 1.7 }}>
                                {appliedSearch
                                  ? <>No admin activity matches <strong>&ldquo;{appliedSearch}&rdquo;</strong>. Try a different search term.</>
                                  : 'No admin actions have been recorded yet. Activity will appear here once admins take actions on identities.'}
                              </Typography>
                              {appliedSearch && (
                                <Box sx={{
                                  display: 'inline-flex', alignItems: 'center', gap: 0.7,
                                  px: 1.5, py: 0.6, borderRadius: 6,
                                  bgcolor: '#ede9fe', border: '1px solid #c4b5fd',
                                  mt: 0.5,
                                }}>
                                  <Iconify icon="eva:search-fill" width={13} height={13} sx={{ color: '#7c3aed' }} />
                                  <Typography variant="caption" sx={{ color: '#6d28d9', fontWeight: 700, fontSize: 11 }}>&ldquo;{appliedSearch}&rdquo;</Typography>
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Scrollbar>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={activityTotalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          ) : TABS[activeTab]?.isSsnTab ? (
            /* ── SSN Verification Request Table ── */
            <>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 800 }}>
                  <Table>
                    <ListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={SSN_TABLE_HEAD}
                      rowCount={ssnRecords.length}
                      numSelected={0}
                      onRequestSort={handleRequestSort}
                      hideCheckboxColumnHeader
                    />
                    <TableBody>
                      {ssnRecords.map((row, idx) => {
                        const fullName = row.userFullName || row.fullName ||
                          `${row.firstName || ''} ${row.lastName || ''}`.trim() ||
                          row.user?.fullName || `${row.user?.firstName || ''} ${row.user?.lastName || ''}`.trim();
                        const userId   = row.userId   ?? row.user?.userId   ?? '-';
                        const email    = row.email    ?? row.user?.email    ?? '-';
                        const currentSsn = row.currentSsn ?? row.oldSsn ?? row.ssn ?? '-';
                        const newSsn     = row.newSsn ?? row.requestedSsn ?? '-';
                        const status     = row.status ?? row.requestStatus ?? null;
                        const requestedAt = row.requestedAt ?? row.createdAt ?? row.uploadedAt ?? null;
                        const statusCfg  = STATUS_CONFIG[status] ?? { label: status ?? 'Unknown', color: 'default' };
                        const initials   = fullName
                          ? fullName.split(' ').filter(Boolean).map((n) => n[0]).join('').toUpperCase().slice(0, 2)
                          : '?';
                        const isHighlighted = highlightId !== null && (
                          String(row.id) === highlightId ||
                          String(row.requestId) === highlightId
                        );
                        return (
                          <TableRow
                            hover
                            key={row.id ?? row.requestId ?? idx}
                            tabIndex={-1}
                            ref={(el) => { if (isHighlighted && el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
                            sx={{
                              '&:last-child td': { border: 0 },
                              bgcolor: isHighlighted ? 'rgba(14,165,233,0.10)' : idx % 2 !== 0 ? 'rgba(240,249,255,0.5)' : 'transparent',
                              outline: isHighlighted ? '2.5px solid #0284c7' : 'none',
                              transition: 'background 0.15s, box-shadow 0.15s',
                              '&:hover': { bgcolor: '#e0f2fe !important', boxShadow: 'inset 3px 0 0 #0284c7' },
                              animation: isHighlighted ? 'rowBloom 2.2s ease-out 0.15s both' : 'none',
                            }}
                          >
                            {/* Name */}
                            <TableCell sx={{ py: 1.4 }}>
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Avatar sx={{
                                  width: 36, height: 36, fontSize: 13, fontWeight: 800, flexShrink: 0,
                                  background: 'linear-gradient(135deg, #0284c7, #38bdf8)',
                                  color: '#fff', boxShadow: '0 2px 8px rgba(2,132,199,0.3)',
                                  border: '2px solid rgba(255,255,255,0.8)',
                                }}>
                                  {initials}
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: 13, lineHeight: 1.3 }}>{fullName || '-'}</Typography>
                                  <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Iconify icon="eva:email-outline" width={11} height={11} color="text.disabled" />
                                    <Typography variant="caption" color="text.disabled" noWrap sx={{ fontSize: 10 }}>{email}</Typography>
                                  </Stack>
                                </Box>
                              </Stack>
                            </TableCell>
                            {/* User ID */}
                            <TableCell sx={{ py: 1.4 }}>
                              <Box sx={{ display: 'inline-block', px: 1, py: 0.3, borderRadius: 1, bgcolor: 'grey.100', border: '1px solid', borderColor: 'grey.300' }}>
                                <Typography sx={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: 'text.secondary', lineHeight: 1 }}>
                                  {userId}
                                </Typography>
                              </Box>
                            </TableCell>
                            {/* Email */}
                            <TableCell sx={{ py: 1.4 }}>
                              <Stack direction="row" alignItems="center" spacing={0.6}>
                                <Iconify icon="eva:email-outline" width={13} height={13} color="text.disabled" />
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: 12 }}>{email}</Typography>
                              </Stack>
                            </TableCell>
                            {/* Current SSN */}
                            <TableCell sx={{ py: 1.4 }}>
                              <Box sx={{ px: 1.2, py: 0.4, borderRadius: 1, bgcolor: '#f1f5f9', border: '1px solid', borderColor: '#cbd5e1', display: 'inline-block' }}>
                                <Typography sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: '#475569', lineHeight: 1 }}>{currentSsn}</Typography>
                              </Box>
                            </TableCell>
                            {/* New SSN */}
                            <TableCell sx={{ py: 1.4 }}>
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
                                <Box sx={{ p: 0.35, bgcolor: '#dcfce7', borderRadius: '50%', display: 'flex', border: '1px solid #86efac' }}>
                                  <Iconify icon="eva:arrow-forward-fill" width={9} height={9} sx={{ color: '#16a34a' }} />
                                </Box>
                                <Box sx={{ px: 1.2, py: 0.4, borderRadius: 1, bgcolor: '#f0fdf4', border: '1.5px solid', borderColor: '#86efac', display: 'inline-block' }}>
                                  <Typography sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 800, color: '#15803d', lineHeight: 1 }}>{newSsn}</Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            {/* Status */}
                            <TableCell sx={{ py: 1.4 }}>
                              {status !== null ? (
                                <Box sx={{
                                  display: 'inline-flex', alignItems: 'center', gap: 0.6,
                                  px: 1.1, py: 0.45, borderRadius: 6,
                                  bgcolor: status === 0 ? '#fffbeb' : status === 1 ? '#f0fdf4' : '#fef2f2',
                                  border: '1.5px solid',
                                  borderColor: status === 0 ? '#fde68a' : status === 1 ? '#86efac' : '#fca5a5',
                                }}>
                                  <Box sx={{
                                    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                                    bgcolor: status === 0 ? '#d97706' : status === 1 ? '#16a34a' : '#dc2626',
                                    ...(status === 0 && {
                                      '@keyframes statusPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                                      animation: 'statusPulse 1.8s ease-in-out infinite',
                                    }),
                                  }} />
                                  <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1, color: status === 0 ? '#92400e' : status === 1 ? '#14532d' : '#7f1d1d' }}>
                                    {statusCfg.label}
                                  </Typography>
                                </Box>
                              ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                            </TableCell>
                            {/* Requested At */}
                            <TableCell sx={{ py: 1.4 }}>
                              <Tooltip title={requestedAt ? new Date(requestedAt).toLocaleString('en-GB') : ''} arrow>
                                <Box>
                                  {requestedAt ? (
                                    <>
                                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: 12, lineHeight: 1.3 }}>
                                        {(() => { const diff = Date.now() - new Date(requestedAt).getTime(); const h = diff / 3600000; const d = Math.floor(h / 24); return h < 1 ? `${Math.floor(diff / 60000)}m ago` : h < 24 ? `${Math.floor(h)}h ago` : d < 7 ? `${d}d ago` : new Date(requestedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); })()}
                                      </Typography>
                                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                                        {new Date(requestedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                      </Typography>
                                    </>
                                  ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                                </Box>
                              </Tooltip>
                            </TableCell>
                            {/* Actions */}
                            <TableCell sx={{ py: 1.4 }}>
                              <Tooltip title="View SSN Request Details" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleSsnView(row)}
                                  sx={{
                                    width: 32, height: 32,
                                    background: 'linear-gradient(135deg, #0284c7, #38bdf8)',
                                    color: '#fff',
                                    boxShadow: '0 2px 8px rgba(2,132,199,0.35)',
                                    '&:hover': { background: 'linear-gradient(135deg, #0369a1, #0284c7)', boxShadow: '0 4px 14px rgba(2,132,199,0.45)', transform: 'translateY(-1px)' },
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <Iconify icon="eva:eye-fill" width={16} height={16} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    {!ssnLoading && ssnRecords.length === 0 && (
                      <TableBody>
                        <TableRow>
                          <TableCell align="center" colSpan={SSN_TABLE_HEAD.length} sx={{ py: 0, border: 0 }}>
                            <Box sx={{
                              py: 8, px: 4,
                              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
                              background: 'linear-gradient(180deg, #f0f9ff 0%, #fff 100%)',
                            }}>
                              <Box sx={{
                                position: 'relative', mb: 1,
                                '@keyframes floatSsn': {
                                  '0%,100%': { transform: 'translateY(0px)' },
                                  '50%': { transform: 'translateY(-8px)' },
                                },
                              }}>
                                <Box sx={{
                                  width: 72, height: 72, borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #bae6fd, #7dd3fc)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  boxShadow: '0 8px 28px rgba(2,132,199,0.18)',
                                  animation: 'floatSsn 3s ease-in-out infinite',
                                }}>
                                  <Iconify icon="eva:person-done-outline" width={34} height={34} sx={{ color: '#0369a1' }} />
                                </Box>
                                <Box sx={{
                                  position: 'absolute', bottom: 2, right: -2,
                                  width: 24, height: 24, borderRadius: '50%',
                                  bgcolor: '#fff', border: '2px solid #bae6fd',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                }}>
                                  <Iconify icon="eva:search-outline" width={12} height={12} sx={{ color: '#0284c7' }} />
                                </Box>
                              </Box>
                              <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#0c4a6e', fontSize: 15 }}>
                                No SSN Requests Found
                              </Typography>
                              <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 300, textAlign: 'center', lineHeight: 1.7 }}>
                                {appliedSearch
                                  ? <>No SSN update requests match <strong>&ldquo;{appliedSearch}&rdquo;</strong>. Try adjusting your search.</>
                                  : 'No SSN update requests have been submitted yet. New requests from users will appear here.'}
                              </Typography>
                              {appliedSearch && (
                                <Box sx={{
                                  display: 'inline-flex', alignItems: 'center', gap: 0.7,
                                  px: 1.5, py: 0.6, borderRadius: 6,
                                  bgcolor: '#e0f2fe', border: '1px solid #7dd3fc',
                                  mt: 0.5,
                                }}>
                                  <Iconify icon="eva:search-fill" width={13} height={13} sx={{ color: '#0284c7' }} />
                                  <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 700, fontSize: 11 }}>&ldquo;{appliedSearch}&rdquo;</Typography>
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Scrollbar>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={ssnTotalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          ) : (
            <>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 800 }}>
                  <Table>
                    <ListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={
                        (TABS[activeTab]?.status === 2 || TABS[activeTab]?.status === 3 || TABS[activeTab]?.status === null)
                          ? [...TABLE_HEAD.slice(0, -1), { id: 'rejectionReason', label: 'Reason', alignRight: false }, TABLE_HEAD[TABLE_HEAD.length - 1]]
                          : TABLE_HEAD
                      }
                      rowCount={records.length}
                      numSelected={0}
                      onRequestSort={handleRequestSort}
                      hideCheckboxColumnHeader
                    />
                    <TableBody>
                      {records.map((row) => {
                        const { documentId, user } = row;
                        const rejectionReason = row.rejectionReason ?? row.documents?.[0]?.rejectionReason ?? null;
                        const uploadedAt = user?.lastUploadedAt ?? row.uploadedAt;
                        const status = user?.overallStatus ?? row.status;
                        const fullName = user?.fullName?.trim() || `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
                        const statusCfg = STATUS_CONFIG[status] ?? { label: status, color: 'default' };
                        const initials = fullName
                          .split(' ')
                          .filter(Boolean)
                          .map((n) => n[0])
                          .join('')
                          .toUpperCase()
                          .slice(0, 2);
                        const isHighlighted = highlightId !== null && (
                          String(row.requestId) === highlightId ||
                          String(user?.requestId) === highlightId ||
                          String(user?.userId) === highlightId ||
                          String(documentId) === highlightId
                        );
                        return (
                          <TableRow
                            hover
                            key={documentId}
                            tabIndex={-1}
                            ref={(el) => { if (isHighlighted && el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
                            sx={{
                              '&:last-child td': { border: 0 },
                              backgroundColor: isHighlighted ? 'rgba(25,118,210,0.10)' : getRowAgeBg(uploadedAt),
                              outline: isHighlighted ? '2.5px solid #1976d2' : 'none',
                              outlineOffset: '-2px',
                              '@keyframes rowBloom': {
                                '0%':   { boxShadow: 'inset 0 0 0 0 rgba(25,118,210,0.5)',  backgroundColor: 'rgba(25,118,210,0.30)' },
                                '40%':  { boxShadow: 'inset 0 0 28px 8px rgba(25,118,210,0.22)', backgroundColor: 'rgba(25,118,210,0.18)' },
                                '100%': { boxShadow: 'inset 0 0 0 0 rgba(25,118,210,0)',   backgroundColor: 'rgba(25,118,210,0.10)' },
                              },
                              animation: isHighlighted ? 'rowBloom 2.2s ease-out 0.15s both' : 'none',
                            }}
                          >
                            <TableCell sx={{ py: 1.4 }}>
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Avatar sx={{
                                  width: 36, height: 36, fontSize: 13, fontWeight: 800, flexShrink: 0,
                                  background: status === 1
                                    ? 'linear-gradient(135deg, #2e7d32, #43a047)'
                                    : status === 2
                                    ? 'linear-gradient(135deg, #c62828, #ef5350)'
                                    : status === 3
                                    ? 'linear-gradient(135deg, #e65100, #ef6c00)'
                                    : 'linear-gradient(135deg, #e65100, #ff8f00)',
                                  color: '#fff',
                                  boxShadow: status === 1
                                    ? '0 2px 8px rgba(46,125,50,0.3)'
                                    : status === 2
                                    ? '0 2px 8px rgba(198,40,40,0.3)'
                                    : '0 2px 8px rgba(230,81,0,0.3)',
                                  border: '2px solid rgba(255,255,255,0.8)',
                                }}>
                                  {initials}
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: 13, lineHeight: 1.3 }}>
                                    {fullName || '-'}
                                  </Typography>
                                  {user?.profession ? (
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                      <Iconify icon="eva:briefcase-outline" width={10} height={10} color="text.disabled" />
                                      <Typography variant="caption" color="text.disabled" noWrap sx={{ fontSize: 10 }}>
                                        {user.profession}
                                      </Typography>
                                    </Stack>
                                  ) : null}
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ py: 1.4 }}>
                              <Box sx={{ display: 'inline-block', px: 1, py: 0.3, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.04)', border: '1px solid', borderColor: 'grey.300' }}>
                                <Typography sx={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: 'text.secondary', lineHeight: 1 }}>
                                  {user?.userId ?? '-'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 1.4 }}>
                              <Stack direction="row" alignItems="center" spacing={0.6}>
                                <Iconify icon="eva:email-outline" width={13} height={13} color="text.disabled" />
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: 12 }}>
                                  {user?.email || '-'}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ py: 1.4 }}>
                              {user?.city ? (
                                <Stack direction="row" alignItems="center" spacing={0.6}>
                                  <Iconify icon="eva:pin-outline" width={13} height={13} color="text.disabled" />
                                  <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: 12 }}>{user.city}</Typography>
                                </Stack>
                              ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                            </TableCell>
                            <TableCell sx={{ py: 1.4 }}>
                              <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.6,
                                px: 1.1, py: 0.45, borderRadius: 6,
                                bgcolor: status === 0 ? '#fffbeb' : status === 1 ? '#f0fdf4' : status === 2 ? '#fef2f2' : '#fff7ed',
                                border: '1.5px solid',
                                borderColor: status === 0 ? '#fde68a' : status === 1 ? '#86efac' : status === 2 ? '#fca5a5' : '#fdba74',
                              }}>
                                <Box sx={{
                                  width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                                  bgcolor: status === 0 ? '#d97706' : status === 1 ? '#16a34a' : status === 2 ? '#dc2626' : '#ea580c',
                                  ...(status === 0 && {
                                    '@keyframes bkdPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
                                    animation: 'bkdPulse 1.8s ease-in-out infinite',
                                  }),
                                }} />
                                <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1, color: status === 0 ? '#92400e' : status === 1 ? '#14532d' : status === 2 ? '#7f1d1d' : '#7c2d12' }}>
                                  {statusCfg.label}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 1.4 }}>
                              <Tooltip title={uploadedAt ? new Date(uploadedAt).toLocaleString('en-GB') : ''} arrow>
                                <Box>
                                  {uploadedAt ? (
                                    <>
                                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: 12, lineHeight: 1.3 }}>
                                        {(() => { const diff = Date.now() - new Date(uploadedAt).getTime(); const h = diff / 3600000; const d = Math.floor(h / 24); return h < 1 ? `${Math.floor(diff / 60000)}m ago` : h < 24 ? `${Math.floor(h)}h ago` : d < 7 ? `${d}d ago` : new Date(uploadedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); })()}
                                      </Typography>
                                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                                        {new Date(uploadedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                      </Typography>
                                    </>
                                  ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                                </Box>
                              </Tooltip>
                            </TableCell>
                            {/* Reason column — on Rejected, Partial Rejected and All tabs */}
                            {(TABS[activeTab]?.status === 2 || TABS[activeTab]?.status === 3 || TABS[activeTab]?.status === null) && (
                              <TableCell sx={{ maxWidth: 200, py: 1.4 }}>
                                {rejectionReason ? (
                                  <Tooltip title={rejectionReason} arrow placement="top">
                                    <Box sx={{
                                      display: 'inline-flex', alignItems: 'center', gap: 0.6,
                                      px: 1, py: 0.35, borderRadius: 1,
                                      bgcolor: '#fef2f2', border: '1px solid #fca5a5', maxWidth: 190, overflow: 'hidden',
                                    }}>
                                      <Iconify icon="eva:message-circle-outline" width={11} height={11} sx={{ color: '#dc2626', flexShrink: 0 }} />
                                      <Typography variant="caption" sx={{ color: '#b91c1c', fontWeight: 600, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {rejectionReason}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  <Typography variant="body2" color="text.disabled">—</Typography>
                                )}
                              </TableCell>
                            )}
                            <TableCell sx={{ py: 1.4 }}>
                              <Tooltip title="Preview Documents" arrow>
                                <IconButton
                                  onClick={() => handleDownload(row)}
                                  size="small"
                                  sx={{
                                    width: 32, height: 32,
                                    background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                                    color: '#fff',
                                    boxShadow: '0 2px 8px rgba(25,118,210,0.35)',
                                    '&:hover': { background: 'linear-gradient(135deg, #1565c0, #1976d2)', boxShadow: '0 4px 14px rgba(25,118,210,0.45)', transform: 'translateY(-1px)' },
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <Iconify icon="eva:eye-fill" width={16} height={16} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>

                    {isNotFound && (
                      <TableBody>
                        {(() => {
                          const tab = TABS[activeTab] ?? {};
                      const colSpan = (tab.status === 2 || tab.status === 3 || tab.status === null) ? 8 : 7;
                      const iconByStatus = {
                        0: { icon: 'eva:clock-outline',              bg: 'linear-gradient(135deg,#fef3c7,#fde68a)', color: '#92400e', shadow: 'rgba(245,158,11,0.18)', title: 'No Pending Requests',     body: 'No documents are waiting for review. All pending verifications will appear here.' },
                        1: { icon: 'eva:checkmark-circle-2-outline', bg: 'linear-gradient(135deg,#dcfce7,#bbf7d0)', color: '#14532d', shadow: 'rgba(34,197,94,0.18)',   title: 'No Accepted Records',     body: 'No verification requests have been approved yet. Accepted documents will show up here.' },
                        2: { icon: 'eva:close-circle-outline',       bg: 'linear-gradient(135deg,#fee2e2,#fca5a5)', color: '#7f1d1d', shadow: 'rgba(239,68,68,0.18)',   title: 'No Rejected Records',     body: 'No verification requests have been rejected. Rejected documents will appear here.' },
                        3: { icon: 'eva:alert-triangle-outline',     bg: 'linear-gradient(135deg,#ffedd5,#fdba74)', color: '#7c2d12', shadow: 'rgba(249,115,22,0.18)', title: 'No Partial Rejections',   body: 'No partial rejections exist. Requests with some rejected documents will appear here.' },
                      };
                      const cfg = tab.status !== null ? (iconByStatus[tab.status] ?? iconByStatus[0]) : { icon: 'eva:layers-outline', bg: 'linear-gradient(135deg,#e0e7ff,#c7d2fe)', color: '#1e1b4b', shadow: 'rgba(99,102,241,0.18)', title: 'No Records Found', body: 'No verification requests exist yet.' };
                      return (
                        <TableRow>
                          <TableCell align="center" colSpan={colSpan} sx={{ py: 0, border: 0 }}>
                            <Box sx={{
                              py: 8, px: 4,
                              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
                              background: `linear-gradient(180deg, ${tab.bg ?? '#f8fafc'} 0%, #fff 100%)`,
                            }}>
                              <Box sx={{ position: 'relative', mb: 1 }}>
                                <Box sx={{
                                  width: 76, height: 76, borderRadius: '50%',
                                  background: cfg.bg,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  boxShadow: `0 10px 32px ${cfg.shadow}`,
                                  '@keyframes floatBkd': {
                                    '0%,100%': { transform: 'translateY(0px)' },
                                    '50%': { transform: 'translateY(-9px)' },
                                  },
                                  animation: 'floatBkd 3.2s ease-in-out infinite',
                                }}>
                                  <Iconify icon={cfg.icon} width={36} height={36} sx={{ color: cfg.color }} />
                                </Box>
                                {appliedSearch && (
                                  <Box sx={{
                                    position: 'absolute', bottom: 2, right: -4,
                                    width: 26, height: 26, borderRadius: '50%',
                                    bgcolor: '#fff', border: `2px solid ${tab.color ?? '#6366f1'}40`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    '@keyframes spinSearch': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
                                    animation: 'spinSearch 4s linear infinite',
                                  }}>
                                    <Iconify icon="eva:search-outline" width={13} height={13} sx={{ color: tab.color ?? '#6366f1' }} />
                                  </Box>
                                )}
                              </Box>
                              <Typography variant="subtitle1" fontWeight={800} sx={{ color: cfg.color, fontSize: 15 }}>
                                {appliedSearch ? 'No Results Found' : cfg.title}
                              </Typography>
                              <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 320, textAlign: 'center', lineHeight: 1.75 }}>
                                {appliedSearch
                                  ? <>
                                      No {tab.label?.toLowerCase() ?? 'records'} match{' '}
                                      <strong>&ldquo;{appliedSearch}&rdquo;</strong>.
                                      {' '}Try clearing the search or changing the search column.
                                    </>
                                  : cfg.body}
                              </Typography>
                              {appliedSearch && (
                                <Box sx={{
                                  display: 'inline-flex', alignItems: 'center', gap: 0.8,
                                  px: 1.6, py: 0.65, borderRadius: 6,
                                  bgcolor: `${tab.color ?? '#6366f1'}12`,
                                  border: '1.5px solid', borderColor: `${tab.color ?? '#6366f1'}35`,
                                  mt: 0.5,
                                }}>
                                  <Iconify icon="eva:funnel-outline" width={13} height={13} sx={{ color: tab.color ?? '#6366f1' }} />
                                  <Typography variant="caption" sx={{ color: tab.color ?? '#6366f1', fontWeight: 700, fontSize: 11 }}>
                                    Searching in: {TABS[activeTab]?.isSsnTab
                                      ? (SSN_SEARCH_COLUMN_OPTIONS.find((o) => o.value === searchColumn)?.label ?? 'All fields')
                                      : (SEARCH_COLUMN_OPTIONS.find((o) => o.value === searchColumn)?.label ?? 'All fields')}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>&mdash;</Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11, color: tab.color ?? '#6366f1' }}>&ldquo;{appliedSearch}&rdquo;</Typography>
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                        })()}
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Scrollbar>

              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />

              {/* Color Legend */}
              <Box sx={{ mx: 2.5, mb: 2, mt: 1, p: 1.5, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                <Stack direction="row" alignItems="center" spacing={0.8} mb={1}>
                  <Iconify icon="eva:info-outline" width={13} height={13} color="text.disabled" />
                  <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 9.5 }}>
                    Row Age Indicator — based on upload time
                  </Typography>
                </Stack>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {ROW_AGE_CONFIG.map((cfg, i) => {
                    const dotColors = ['#4caf50', '#ffa000', '#f57c00', '#e53935'];
                    return (
                      <Box key={cfg.label} sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.7,
                        px: 1.2, py: 0.45, borderRadius: 6,
                        bgcolor: cfg.bg, border: '1px solid rgba(0,0,0,0.1)',
                      }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dotColors[i], flexShrink: 0, border: '1px solid rgba(0,0,0,0.12)' }} />
                        <Typography variant="caption" fontWeight={700} sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1 }}>{cfg.label}</Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </>
          )}
        </Card>
      </Container>

      {/* Document Preview Modal */}
      <Modal open={previewOpen} onClose={(e, reason) => { if (reason !== 'backdropClick') handleClosePreview(); }}>
        <Box sx={modalStyle}>
          {/* Modal Header */}
          <Box sx={{
            px: 3, py: 1.8,
            background: 'linear-gradient(90deg, #f8fafc 0%, #eef2ff 100%)',
            borderBottom: '1.5px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1.8}>
                <Box sx={{
                  p: 1, borderRadius: 2,
                  background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                  display: 'flex', boxShadow: '0 2px 8px rgba(25,118,210,0.35)',
                }}>
                  <Iconify icon="eva:file-text-fill" width={20} height={20} sx={{ color: '#fff' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ maxWidth: 500, lineHeight: 1.3 }}>
                    {previewName}
                  </Typography>
                  {activeRow && (
                    <Stack direction="row" alignItems="center" spacing={0.8}>
                      <Iconify icon="eva:email-outline" width={12} height={12} color="text.disabled" />
                      <Typography variant="caption" color="text.secondary">
                        {activeRow.user?.email}
                      </Typography>
                      {activeRow.user?.userId && (
                        <>
                          <Typography variant="caption" color="text.disabled">·</Typography>
                          <Typography variant="caption" color="text.disabled">ID: {activeRow.user.userId}</Typography>
                        </>
                      )}
                    </Stack>
                  )}
                </Box>
              </Stack>
              <IconButton
                onClick={handleClosePreview}
                size="small"
                sx={{
                  bgcolor: 'grey.100', border: '1px solid', borderColor: 'grey.300',
                  '&:hover': { bgcolor: 'error.lighter', borderColor: 'error.light', color: 'error.main' },
                  transition: 'all 0.2s',
                }}
              >
                <Iconify icon="eva:close-fill" width={18} height={18} />
              </IconButton>
            </Stack>
          </Box>

          {/* Modal Body */}
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'row' }}>

            {/* Left — Document Panel */}
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              {previewLoading ? (
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height={360} gap={2}>
                  <CircularProgress size={40} thickness={4} />
                  <Typography variant="body2" color="text.secondary">Loading document…</Typography>
                </Box>
              ) : docList.length > 0 ? (
                <>
                  {/* Inner document tabs + profile toggle button */}
                  <Box sx={{
                    px: 2, borderBottom: '1.5px solid', borderColor: 'divider',
                    bgcolor: '#fafbfc', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    minHeight: 48,
                  }}>
                    <Tabs
                      value={activeDocTab}
                      onChange={(_, v) => setActiveDocTab(v)}
                      sx={{
                        flex: 1, minWidth: 0,
                        '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', background: 'linear-gradient(90deg,#1976d2,#42a5f5)' },
                        '& .MuiTab-root': { fontWeight: 600, fontSize: 13, color: 'text.secondary', minHeight: 48 },
                        '& .Mui-selected': { color: 'primary.main' },
                      }}
                    >
                      {docList.map((doc, idx) => (
                        <Tab
                          key={doc.documentId ?? idx}
                          label={doc.documentName || `Document ${idx + 1}`}
                          icon={<Iconify icon="eva:file-text-outline" width={15} height={15} />}
                          iconPosition="start"
                          sx={{ minHeight: 48, gap: 0.6 }}
                        />
                      ))}
                    </Tabs>
                    <Tooltip title={profilePanelOpen ? 'Hide Profile' : 'View User Profile'} arrow>
                      <span>
                        <IconButton
                          onClick={handleToggleProfile}
                          size="small"
                          disabled={profileLoading}
                          sx={{
                            ml: 1.5, mr: 0.5, flexShrink: 0, width: 34, height: 34,
                            background: profilePanelOpen
                              ? 'linear-gradient(135deg,#1565c0,#42a5f5)'
                              : 'rgba(25,118,210,0.08)',
                            color: profilePanelOpen ? '#fff' : 'primary.main',
                            border: '1.5px solid',
                            borderColor: profilePanelOpen ? 'primary.dark' : 'primary.light',
                            boxShadow: profilePanelOpen ? '0 2px 10px rgba(25,118,210,0.35)' : 'none',
                            '&:hover': {
                              background: profilePanelOpen
                                ? 'linear-gradient(135deg,#0d47a1,#1976d2)'
                                : 'rgba(25,118,210,0.15)',
                            },
                            transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
                          }}
                        >
                          {profileLoading
                            ? <CircularProgress size={15} color="inherit" />
                            : <Iconify icon="eva:person-fill" width={17} height={17} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  {/* Active document preview */}
                  {(() => {
                    const doc = docList[activeDocTab];
                    const url = doc?.url ?? '';
                    const isPdf = /\.pdf(\?|$)/i.test(url);
                    const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)(\?|$)/i.test(url);
                    return (
                      <Box sx={{ flex: 1, overflow: 'hidden', p: 1.5, bgcolor: 'grey.50', display: 'flex', flexDirection: 'column' }}>
                        {isPdf ? (
                          <Box sx={{
                            flex: 1, borderRadius: 2, overflow: 'hidden',
                            boxShadow: '0 2px 16px rgba(0,0,0,0.1)',
                            border: '1px solid', borderColor: 'grey.200',
                            transition: 'box-shadow 0.3s',
                            '&:hover': { boxShadow: '0 4px 24px rgba(0,0,0,0.15)' },
                          }}>
                            <iframe src={url} title={doc?.documentName} width="100%" height="100%" style={{ border: 'none', display: 'block' }} />
                          </Box>
                        ) : isImage ? (
                          <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                            <Box component="img" src={url} alt={doc?.documentName} sx={{
                              maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
                              borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                              transition: 'transform 0.3s, box-shadow 0.3s',
                              '&:hover': { transform: 'scale(1.01)', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' },
                            }} />
                          </Box>
                        ) : url ? (
                          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} gap={2}>
                            <Iconify icon="eva:file-outline" width={48} height={48} color="text.disabled" />
                            <Typography color="text.secondary">Preview not available for this file type.</Typography>
                            <a href={url} download={doc?.documentName} style={{ textDecoration: 'none' }}>
                              <Button variant="outlined" startIcon={<Iconify icon="eva:download-fill" width={18} height={18} />}>Download File</Button>
                            </a>
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })()}
                </>
              ) : null}
            </Box>

            {/* Right — Profile Panel (slides in) */}
            <Box sx={{
              width: profilePanelOpen ? 380 : 0,
              flexShrink: 0,
              overflow: 'hidden',
              transition: 'width 0.38s cubic-bezier(0.4, 0, 0.2, 1)',
              borderLeft: '1.5px solid',
              borderColor: 'divider',
              background: '#f8fafc',
            }}>
              <Box sx={{ width: 380, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {profileLoading ? (
                  <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flex={1} gap={2}>
                    <CircularProgress size={32} thickness={4} />
                    <Typography variant="caption" color="text.secondary">Loading profile…</Typography>
                  </Box>
                ) : profileData ? (
                  <>
                    {/* Profile Header */}
                    {(() => {
                      const u = profileData;
                      const fullName = u.fullName?.trim() || `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown';
                      const initials = fullName.split(' ').filter(Boolean).map((n) => n[0]).join('').toUpperCase().slice(0, 2);
                      const overallCfg = STATUS_CONFIG[u.overallStatus] ?? null;
                      return (
                        <Box sx={{
                          background: 'linear-gradient(145deg, #0d47a1 0%, #1976d2 50%, #42a5f5 100%)',
                          px: 2.5, pt: 2.5, pb: 3, flexShrink: 0,
                          position: 'relative', overflow: 'hidden',
                        }}>
                          {/* Decorative circle */}
                          <Box sx={{
                            position: 'absolute', top: -28, right: -28,
                            width: 110, height: 110, borderRadius: '50%',
                            bgcolor: 'rgba(255,255,255,0.07)',
                            pointerEvents: 'none',
                          }} />
                          <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.6)', letterSpacing: 1.4, display: 'block', mb: 1.5, fontSize: 9 }}>
                            User Profile
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar sx={{
                              width: 58, height: 58, fontSize: 20, fontWeight: 800,
                              bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                              border: '2.5px solid rgba(255,255,255,0.55)',
                              boxShadow: '0 4px 16px rgba(0,0,0,0.22)',
                              letterSpacing: 1,
                            }}>
                              {initials}
                            </Avatar>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="subtitle1" fontWeight={800} color="#fff" noWrap sx={{ lineHeight: 1.25, fontSize: 15 }}>
                                {fullName}
                              </Typography>
                              {u.email && (
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)', display: 'block', mt: 0.3 }} noWrap>
                                  {u.email}
                                </Typography>
                              )}
                              {overallCfg && (
                                <Box mt={0.9}>
                                  <Chip
                                    label={overallCfg.label}
                                    color={overallCfg.color}
                                    size="small"
                                    sx={{ fontWeight: 700, fontSize: 10, height: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.18)' }}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Stack>
                        </Box>
                      );
                    })()}
                    {/* Profile Details */}
                    {(() => {
                      const u = profileData;
                      const fields = [
                        { icon: 'eva:hash-outline',           label: 'User ID',           value: u.userId },
                        { icon: 'eva:phone-outline',          label: 'Phone',             value: u.phoneNo },
                        { icon: 'eva:person-done-outline',    label: 'SSN',               value: u.ssn },
                        { icon: 'eva:calendar-outline',       label: 'Date of Birth',     value: u.dateOfBirth },
                        { icon: 'eva:pin-outline',            label: 'City',              value: u.city },
                        { icon: 'eva:map-outline',            label: 'Address',           value: u.address },
                        { icon: 'eva:globe-2-outline',        label: 'Country',           value: u.country },
                        { icon: 'eva:navigation-2-outline',   label: 'Zip Code',          value: u.zipCode },
                        { icon: 'eva:briefcase-outline',      label: 'Profession',        value: u.profession },
                        { icon: 'eva:award-outline',          label: 'Organisation',      value: u.organizationName || u.organisation },
                        { icon: 'eva:percent-outline',        label: 'VAT Number',        value: u.vatNumber },
                        { icon: 'eva:clock-outline',          label: 'Registered At',     value: u.registeredAt ? new Date(u.registeredAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null },
                        { icon: 'eva:cloud-upload-outline',   label: 'Last Uploaded',     value: u.lastUploadedAt ? new Date(u.lastUploadedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null },
                        { icon: 'eva:file-text-outline',      label: 'Total Documents',   value: u.totalDocuments },
                        { icon: 'eva:file-outline',           label: 'Invoice Count',     value: u.invoiceCount },
                        { icon: 'eva:checkmark-circle-outline', label: 'SSN Valid',       value: u.isSsnValid === true ? 'Yes' : u.isSsnValid === false ? 'No' : null, bool: true, boolVal: u.isSsnValid },
                        { icon: 'eva:shield-outline',         label: 'Identity Verified', value: u.isIdentityVerified === true ? 'Yes' : u.isIdentityVerified === false ? 'No' : null, bool: true, boolVal: u.isIdentityVerified },
                        { icon: 'eva:link-2-outline',         label: 'Request ID',        value: u.requestId },
                      ].filter((f) => f.value !== null && f.value !== undefined && String(f.value).trim() !== '');

                      return (
                        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
                          <Typography variant="overline" color="text.disabled" fontWeight={700} sx={{ letterSpacing: 1.2, display: 'block', mb: 1.5, fontSize: 9.5 }}>
                            Details
                          </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                            {fields.map((field) => (
                              <Box
                                key={field.label}
                                sx={{
                                  display: 'flex', flexDirection: 'column', gap: 0.5,
                                  p: 1.3, borderRadius: 2,
                                  bgcolor: '#fff',
                                  border: '1.5px solid', borderColor: 'grey.200',
                                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                                  transition: 'all 0.18s',
                                  '&:hover': { borderColor: 'primary.light', boxShadow: '0 2px 10px rgba(25,118,210,0.1)', transform: 'translateY(-1px)' },
                                }}
                              >
                                <Stack direction="row" alignItems="center" spacing={0.7}>
                                  <Box sx={{ p: 0.5, bgcolor: 'primary.lighter', borderRadius: 0.8, display: 'flex', flexShrink: 0 }}>
                                    <Iconify icon={field.icon} width={12} height={12} color="primary.dark" />
                                  </Box>
                                  <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 8.5, lineHeight: 1 }}>
                                    {field.label}
                                  </Typography>
                                </Stack>
                                {field.bool ? (
                                  <Chip
                                    label={field.value}
                                    size="small"
                                    color={field.boolVal ? 'success' : 'error'}
                                    sx={{ fontWeight: 700, fontSize: 10, height: 18, alignSelf: 'flex-start', mt: 0.3 }}
                                  />
                                ) : (
                                  <Typography variant="body2" fontWeight={600} color="text.primary" noWrap sx={{ fontSize: 12, lineHeight: 1.3 }} title={String(field.value)}>
                                    {field.value}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      );
                    })()}
                  </>
                ) : null}
              </Box>
            </Box>

          </Box>

          {/* Modal Footer — per-document status */}
          {!previewLoading && docList.length > 0 && (() => {
            const currentDoc = docList[activeDocTab];
            const docStatus = currentDoc?.status ?? 0;
            const docId = currentDoc?.documentId;
            const docName = currentDoc?.documentName || `Document ${activeDocTab + 1}`;

            if (docStatus === 0) {
              return (
                <Box sx={{
                  px: 3, py: 1.8,
                  borderTop: '2px solid', borderColor: 'warning.light',
                  flexShrink: 0,
                  background: 'linear-gradient(90deg, #fffde7 0%, #fff8e1 100%)',
                  '@keyframes pulseBar': {
                    '0%': { borderTopColor: 'rgba(255,183,0,0.5)' },
                    '50%': { borderTopColor: 'rgba(255,183,0,1)' },
                    '100%': { borderTopColor: 'rgba(255,183,0,0.5)' },
                  },
                  animation: 'pulseBar 2.4s ease-in-out infinite',
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{
                        p: 0.7, bgcolor: 'warning.light', borderRadius: 1, display: 'flex',
                        '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
                        animation: 'spin 4s linear infinite',
                      }}>
                        <Iconify icon="eva:clock-fill" width={14} height={14} color="warning.darker" />
                      </Box>
                      <Typography variant="caption" color="warning.darker" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                        Pending Review
                      </Typography>
                      <Typography variant="caption" color="text.disabled">·</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 260, fontStyle: 'italic' }}>
                        {docName}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1.5}>
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={actionLoading}
                        onClick={() => handleOpenReject(docId)}
                        startIcon={<Iconify icon="eva:close-circle-outline" width={17} height={17} />}
                        sx={{
                          borderRadius: 2.5, px: 3, fontWeight: 700,
                          borderWidth: 2, fontSize: 13,
                          transition: 'all 0.2s',
                          '&:hover': { borderWidth: 2, bgcolor: 'error.lighter', boxShadow: '0 4px 14px rgba(211,47,47,0.25)', transform: 'translateY(-1px)' },
                        }}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        disabled={actionLoading}
                        onClick={() => handleAccept(docId)}
                        startIcon={!actionLoading && <Iconify icon="eva:checkmark-circle-2-fill" width={17} height={17} />}
                        sx={{
                          borderRadius: 2.5, px: 3.5, fontWeight: 700, fontSize: 13,
                          background: 'linear-gradient(135deg, #2e7d32, #43a047)',
                          boxShadow: '0 3px 12px rgba(46,125,50,0.35)',
                          transition: 'all 0.2s',
                          '&:hover': { boxShadow: '0 6px 20px rgba(46,125,50,0.45)', background: 'linear-gradient(135deg,#1b5e20,#388e3c)', transform: 'translateY(-1px)' },
                        }}
                      >
                        {actionLoading ? <CircularProgress size={18} color="inherit" /> : 'Approve'}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              );
            }

            const isApproved = docStatus === 1;
            const isRejected = docStatus === 2;
            const statusCfg = STATUS_CONFIG[docStatus] ?? { label: 'Unknown', color: 'default' };
            return (
              <Box sx={{
                px: 3, py: 1.8,
                borderTop: '2px solid',
                borderColor: isApproved ? 'success.light' : isRejected ? 'error.light' : 'warning.light',
                flexShrink: 0,
                background: isApproved
                  ? 'linear-gradient(90deg,#f1f8e9,#e8f5e9)'
                  : isRejected
                  ? 'linear-gradient(90deg,#fce4ec,#ffebee)'
                  : 'linear-gradient(90deg,#fff8e1,#fffde7)',
              }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ p: 0.7, bgcolor: isApproved ? 'success.light' : isRejected ? 'error.light' : 'warning.light', borderRadius: 1, display: 'flex' }}>
                      <Iconify
                        icon={isApproved ? 'eva:checkmark-circle-2-fill' : isRejected ? 'eva:close-circle-fill' : 'eva:alert-triangle-fill'}
                        width={15} height={15}
                        color={isApproved ? 'success.darker' : isRejected ? 'error.darker' : 'warning.darker'}
                      />
                    </Box>
                    <Typography variant="caption" color={isApproved ? 'success.darker' : isRejected ? 'error.darker' : 'warning.darker'} fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                      {statusCfg.label}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">·</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 280, fontStyle: 'italic' }}>
                      {docName}
                    </Typography>
                  </Stack>
                  <Chip
                    label={statusCfg.label}
                    color={statusCfg.color}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: 11, boxShadow: '0 1px 6px rgba(0,0,0,0.12)' }}
                  />
                </Stack>
              </Box>
            );
          })()}
        </Box>
      </Modal>

      {/* Reject Reason Modal */}
      <Modal open={rejectOpen} onClose={() => setRejectOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 480,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: '0 32px 80px rgba(211,47,47,0.22)',
          overflow: 'hidden',
          outline: 'none',
        }}>
          {/* Reject Modal Header */}
          <Box sx={{
            px: 3, py: 2.5,
            background: 'linear-gradient(135deg, #b71c1c 0%, #c62828 45%, #e53935 100%)',
            position: 'relative', overflow: 'hidden', flexShrink: 0,
          }}>
            <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', bottom: -20, left: 80, width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1.8}>
                <Box sx={{
                  p: 1.1, borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  border: '1.5px solid rgba(255,255,255,0.25)',
                  display: 'flex',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                }}>
                  <Iconify icon="eva:close-circle-fill" width={22} height={22} sx={{ color: '#fff' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#fff', lineHeight: 1.2, letterSpacing: '-0.3px' }}>
                    Reject Document
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                    Reason will be shared with the user
                  </Typography>
                </Box>
              </Stack>
              <IconButton
                onClick={() => setRejectOpen(false)}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.12)', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.22)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
                  transition: 'all 0.2s',
                }}
              >
                <Iconify icon="eva:close-fill" width={17} height={17} />
              </IconButton>
            </Stack>
          </Box>

          {/* Reject Modal Body */}
          <Box sx={{ p: 3 }}>
            <Stack direction="row" alignItems="flex-start" spacing={1.2} mb={2.5}
              sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fff5f5', border: '1.5px solid', borderColor: 'error.lighter' }}>
              <Iconify icon="eva:alert-circle-outline" width={18} height={18} color="error.main" sx={{ flexShrink: 0, mt: 0.1 }} />
              <Typography variant="body2" color="error.dark" sx={{ lineHeight: 1.6 }}>
                Please provide a clear and specific reason. The user will receive this message and may need to re-submit their documents.
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.8} mb={1}>
              <Box sx={{ p: 0.45, bgcolor: 'error.lighter', borderRadius: 0.8, display: 'flex' }}>
                <Iconify icon="eva:edit-2-outline" width={13} height={13} color="error.dark" />
              </Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                Rejection Reason <Typography component="span" variant="caption" color="error.main" fontWeight={700}>*</Typography>
              </Typography>
            </Stack>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="E.g. Document is blurry, expired, or incomplete…"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.8,
                  backgroundColor: '#fff',
                  fontSize: 14,
                  lineHeight: 1.6,
                  '& fieldset': { borderWidth: 1.5, borderColor: 'grey.300' },
                  '&:hover fieldset': { borderColor: 'error.main' },
                  '&.Mui-focused fieldset': { borderColor: 'error.main', borderWidth: 2, boxShadow: '0 0 0 3px rgba(211,47,47,0.08)' },
                },
              }}
            />
            {rejectReason.trim() && (
              <Stack direction="row" alignItems="center" spacing={0.6} mt={0.8}>
                <Iconify icon="eva:checkmark-circle-2-fill" width={13} height={13} color="success.main" />
                <Typography variant="caption" color="success.dark" fontWeight={600}>{rejectReason.trim().split(/\s+/).length} word{rejectReason.trim().split(/\s+/).length !== 1 ? 's' : ''} entered</Typography>
              </Stack>
            )}
          </Box>

          {/* Reject Modal Footer */}
          <Box sx={{ px: 3, py: 2, borderTop: '1.5px solid', borderColor: 'divider', background: 'linear-gradient(90deg, #fff5f5, #fff)' }}>
            <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
              <Button
                variant="outlined"
                onClick={() => setRejectOpen(false)}
                disabled={actionLoading}
                startIcon={<Iconify icon="eva:arrow-back-outline" width={16} height={16} />}
                sx={{ borderRadius: 2, borderWidth: 1.5, fontWeight: 700, '&:hover': { borderWidth: 1.5, bgcolor: 'grey.100' } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleRejectSubmit}
                disabled={actionLoading || !rejectReason.trim()}
                startIcon={!actionLoading && <Iconify icon="eva:close-circle-outline" width={18} height={18} />}
                sx={{
                  borderRadius: 2, px: 3.5, fontWeight: 700,
                  background: 'linear-gradient(135deg, #c62828, #e53935)',
                  boxShadow: '0 3px 12px rgba(211,47,47,0.32)',
                  '&:hover:not(:disabled)': { background: 'linear-gradient(135deg,#b71c1c,#c62828)', boxShadow: '0 5px 18px rgba(211,47,47,0.42)', transform: 'translateY(-1px)' },
                  '&:disabled': { opacity: 0.55 },
                  transition: 'all 0.2s',
                }}
              >
                {actionLoading ? <CircularProgress size={18} color="inherit" /> : 'Submit Rejection'}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>
      {/* SSN Detail Modal */}
      <Modal open={ssnDetailOpen} onClose={(_, reason) => { if (reason !== 'backdropClick') handleCloseSsnDetail(); }}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '92%', maxWidth: 580,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: '0 32px 80px rgba(0,0,0,0.22)',
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          maxHeight: '92vh',
          outline: 'none',
        }}>
          {/* SSN Modal Header */}
          <Box sx={{
            px: 3, py: 2.5,
            background: 'linear-gradient(135deg, #0277bd 0%, #0288d1 45%, #29b6f6 100%)',
            position: 'relative', overflow: 'hidden', flexShrink: 0,
          }}>
            <Box sx={{ position: 'absolute', top: -35, right: -35, width: 130, height: 130, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', bottom: -20, left: 60, width: 90, height: 90, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1.8}>
                <Box sx={{
                  p: 1.1, borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  border: '1.5px solid rgba(255,255,255,0.28)',
                  display: 'flex',
                  boxShadow: '0 3px 12px rgba(0,0,0,0.18)',
                }}>
                  <Iconify icon="eva:person-done-fill" width={22} height={22} sx={{ color: '#fff' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#fff', lineHeight: 1.2, letterSpacing: '-0.3px' }}>
                    SSN Update Request
                  </Typography>
                  {ssnActiveRow && (
                    <Stack direction="row" alignItems="center" spacing={0.8} mt={0.3}>
                      <Iconify icon="eva:link-2-outline" width={11} height={11} sx={{ color: 'rgba(255,255,255,0.6)' }} />
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 11 }}>
                        Request {ssnActiveRow.requestId ?? ssnActiveRow.id ?? '—'}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Stack>
              <IconButton
                onClick={handleCloseSsnDetail}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.12)', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.22)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
                  transition: 'all 0.2s',
                }}
              >
                <Iconify icon="eva:close-fill" width={17} height={17} />
              </IconButton>
            </Stack>
          </Box>

          {/* SSN Modal Body */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
            {ssnDetailLoading ? (
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={6} gap={2}>
                <CircularProgress size={40} thickness={4} sx={{ color: '#0288d1' }} />
                <Typography variant="body2" color="text.secondary">Loading request details…</Typography>
              </Box>
            ) : ssnDetailData ? (() => {
              const d = Array.isArray(ssnDetailData) ? ssnDetailData[0] : ssnDetailData;
              const fullName = d?.userFullName || d?.fullName ||
                `${d?.firstName || ''} ${d?.lastName || ''}`.trim() ||
                d?.user?.fullName || `${d?.user?.firstName || ''} ${d?.user?.lastName || ''}`.trim();
              const userId     = d?.userId    ?? d?.user?.userId    ?? '—';
              const email      = d?.email     ?? d?.user?.email     ?? '—';
              const currentSsn = d?.currentSsn ?? d?.oldSsn ?? d?.ssn ?? '—';
              const newSsn     = d?.newSsn     ?? d?.requestedSsn   ?? '—';
              const status     = d?.status     ?? d?.requestStatus  ?? null;
              const requestedAt = d?.requestedAt ?? d?.createdAt ?? d?.uploadedAt ?? null;
              const statusCfg  = STATUS_CONFIG[status] ?? { label: status ?? 'Unknown', color: 'default' };
              const initials   = fullName
                ? fullName.split(' ').filter(Boolean).map((n) => n[0]).join('').toUpperCase().slice(0, 2)
                : '?';
              const fields = [
                { icon: 'eva:hash-outline',          label: 'User ID',       value: userId },
                { icon: 'eva:email-outline',         label: 'Email',         value: email },
                { icon: 'eva:person-done-outline',   label: 'Current SSN',   value: currentSsn, mono: true },
                { icon: 'eva:arrow-forward-outline', label: 'New SSN',       value: newSsn,     mono: true, highlight: true },
                { icon: 'eva:calendar-outline',      label: 'Requested At',  value: requestedAt ? new Date(requestedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null },
                { icon: 'eva:link-2-outline',        label: 'Request ID',    value: d?.requestId ?? d?.id },
              ].filter((f) => f.value !== null && f.value !== undefined && String(f.value).trim() !== '' && String(f.value) !== '—');
              return (
                <Stack spacing={2.5}>
                  {/* User card */}
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 2,
                    p: 2, borderRadius: 2.5,
                    background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
                    border: '1.5px solid', borderColor: '#bae6fd',
                    boxShadow: '0 2px 12px rgba(2,136,209,0.08)',
                  }}>
                    <Avatar sx={{
                      width: 52, height: 52, fontSize: 17, fontWeight: 800,
                      background: 'linear-gradient(135deg, #0288d1, #29b6f6)',
                      color: '#fff',
                      boxShadow: '0 4px 14px rgba(2,136,209,0.35)',
                      border: '2.5px solid rgba(255,255,255,0.7)',
                    }}>
                      {initials}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={800} noWrap sx={{ color: '#01579b', fontSize: 14 }}>{fullName || '—'}</Typography>
                      <Stack direction="row" alignItems="center" spacing={0.6} mt={0.3}>
                        <Iconify icon="eva:email-outline" width={12} height={12} color="#0288d1" />
                        <Typography variant="caption" sx={{ color: '#0277bd' }} noWrap>{email}</Typography>
                      </Stack>
                    </Box>
                    {status !== null && (
                      <Chip
                        label={statusCfg.label}
                        color={statusCfg.color}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: 11, height: 24, flexShrink: 0, boxShadow: '0 1px 6px rgba(0,0,0,0.12)' }}
                      />
                    )}
                  </Box>

                  {/* SSN change highlight */}
                  <Box sx={{
                    borderRadius: 2.5,
                    border: '1.5px solid', borderColor: '#a7f3d0',
                    overflow: 'hidden',
                  }}>
                    <Box sx={{ px: 2, py: 1, bgcolor: '#ecfdf5', borderBottom: '1px solid', borderColor: '#a7f3d0' }}>
                      <Stack direction="row" alignItems="center" spacing={0.8}>
                        <Iconify icon="eva:swap-outline" width={14} height={14} color="#059669" />
                        <Typography variant="caption" fontWeight={800} color="#059669" sx={{ textTransform: 'uppercase', letterSpacing: 0.7, fontSize: 9.5 }}>
                          SSN Change Request
                        </Typography>
                      </Stack>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'stretch', background: 'linear-gradient(90deg, #f0fdf4, #ecfdf5)' }}>
                      <Box sx={{ flex: 1, p: 2, minWidth: 0 }}>
                        <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 9, display: 'block', mb: 0.6 }}>Current SSN</Typography>
                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 15, color: '#374151', letterSpacing: 1 }}>{currentSsn}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, flexShrink: 0 }}>
                        <Box sx={{
                          p: 0.8, borderRadius: '50%',
                          background: 'linear-gradient(135deg, #059669, #34d399)',
                          boxShadow: '0 2px 10px rgba(5,150,105,0.35)',
                          display: 'flex',
                        }}>
                          <Iconify icon="eva:arrow-forward-fill" width={16} height={16} sx={{ color: '#fff' }} />
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1, p: 2, minWidth: 0, borderLeft: '1px dashed', borderColor: '#6ee7b7' }}>
                        <Typography variant="caption" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 9, display: 'block', mb: 0.6, color: '#059669' }}>New SSN</Typography>
                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 15, color: '#065f46', letterSpacing: 1 }}>{newSsn}</Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Other fields */}
                  <Box>
                    <Typography variant="overline" color="text.disabled" fontWeight={700} sx={{ letterSpacing: 1.2, display: 'block', mb: 1.2, fontSize: 9.5 }}>
                      Request Details
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      {fields.filter((f) => !['Current SSN', 'New SSN'].includes(f.label)).map((field) => (
                        <Box key={field.label} sx={{
                          p: 1.5, borderRadius: 2, bgcolor: '#fff',
                          border: '1.5px solid', borderColor: 'grey.200',
                          display: 'flex', flexDirection: 'column', gap: 0.5,
                          transition: 'all 0.18s',
                          '&:hover': { borderColor: '#bae6fd', boxShadow: '0 2px 10px rgba(2,136,209,0.1)', transform: 'translateY(-1px)' },
                        }}>
                          <Stack direction="row" alignItems="center" spacing={0.7}>
                            <Box sx={{ p: 0.5, bgcolor: '#e0f2fe', borderRadius: 0.8, display: 'flex', flexShrink: 0 }}>
                              <Iconify icon={field.icon} width={12} height={12} color="#0277bd" />
                            </Box>
                            <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 8.5, lineHeight: 1 }}>
                              {field.label}
                            </Typography>
                          </Stack>
                          <Typography variant="body2" fontWeight={700} color="text.primary" noWrap sx={{ fontSize: 13, lineHeight: 1.3 }} title={String(field.value)}>
                            {field.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Stack>
              );
            })() : (
              <Box display="flex" justifyContent="center" py={4}>
                <Typography color="text.secondary">No data available</Typography>
              </Box>
            )}
          </Box>

          {/* SSN Modal Footer */}
          {!ssnDetailLoading && ssnDetailData && (
            <Box sx={{
              px: 3, py: 2.2,
              borderTop: '1.5px solid', borderColor: 'divider',
              flexShrink: 0,
              background: 'linear-gradient(90deg, #f0f9ff, #f8fafc)',
            }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={0.8}>
                  <Iconify icon="eva:info-outline" width={14} height={14} color="text.disabled" />
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>Choose an action for this SSN request</Typography>
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => { setSsnConfirmType('reject'); setSsnConfirmOpen(true); }}
                    startIcon={<Iconify icon="eva:close-circle-outline" width={17} height={17} />}
                    sx={{
                      borderRadius: 2.5, px: 3, fontWeight: 700, borderWidth: 2, fontSize: 13,
                      '&:hover': { borderWidth: 2, bgcolor: 'error.lighter', boxShadow: '0 4px 14px rgba(211,47,47,0.22)', transform: 'translateY(-1px)' },
                      transition: 'all 0.2s',
                    }}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => { setSsnConfirmType('approve'); setSsnConfirmOpen(true); }}
                    startIcon={<Iconify icon="eva:checkmark-circle-2-fill" width={17} height={17} />}
                    sx={{
                      borderRadius: 2.5, px: 3.5, fontWeight: 700, fontSize: 13,
                      background: 'linear-gradient(135deg, #2e7d32, #43a047)',
                      boxShadow: '0 3px 12px rgba(46,125,50,0.35)',
                      '&:hover': { boxShadow: '0 6px 20px rgba(46,125,50,0.45)', background: 'linear-gradient(135deg,#1b5e20,#388e3c)', transform: 'translateY(-1px)' },
                      transition: 'all 0.2s',
                    }}
                  >
                    Approve
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}
        </Box>
      </Modal>

      {/* SSN Confirm Modal */}
      <Modal open={ssnConfirmOpen} onClose={() => !ssnActionLoading && setSsnConfirmOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 440,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: `0 32px 80px ${ssnConfirmType === 'approve' ? 'rgba(46,125,50,0.25)' : 'rgba(211,47,47,0.25)'}`,
          overflow: 'hidden',
          outline: 'none',
        }}>
          {/* Confirm Modal Header */}
          <Box sx={{
            px: 3, py: 2.5,
            background: ssnConfirmType === 'approve'
              ? 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 50%, #43a047 100%)'
              : 'linear-gradient(135deg, #b71c1c 0%, #c62828 50%, #e53935 100%)',
            position: 'relative', overflow: 'hidden',
          }}>
            <Box sx={{ position: 'absolute', top: -30, right: -30, width: 110, height: 110, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1.8}>
                <Box sx={{
                  p: 1.1, borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  border: '1.5px solid rgba(255,255,255,0.25)',
                  display: 'flex',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
                }}>
                  <Iconify
                    icon={ssnConfirmType === 'approve' ? 'eva:checkmark-circle-fill' : 'eva:close-circle-fill'}
                    width={22} height={22} sx={{ color: '#fff' }}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#fff', lineHeight: 1.2, letterSpacing: '-0.3px' }}>
                    {ssnConfirmType === 'approve' ? 'Approve SSN Request' : 'Reject SSN Request'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                    {ssnConfirmType === 'approve' ? 'This action will update the user\'s SSN' : 'The user will be notified'}
                  </Typography>
                </Box>
              </Stack>
              <IconButton
                onClick={() => { if (!ssnActionLoading) { setSsnConfirmOpen(false); setSsnRejectReason(''); } }}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.22)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}
              >
                <Iconify icon="eva:close-fill" width={17} height={17} />
              </IconButton>
            </Stack>
          </Box>

          {/* Confirm Modal Body */}
          <Box sx={{ p: 3 }}>
            <Stack direction="row" alignItems="flex-start" spacing={1.2} mb={ssnConfirmType === 'reject' ? 2.5 : 0}
              sx={{
                p: 1.8, borderRadius: 2,
                bgcolor: ssnConfirmType === 'approve' ? '#f0fdf4' : '#fff5f5',
                border: '1.5px solid',
                borderColor: ssnConfirmType === 'approve' ? '#bbf7d0' : '#fecaca',
              }}>
              <Iconify
                icon={ssnConfirmType === 'approve' ? 'eva:shield-outline' : 'eva:alert-triangle-outline'}
                width={20} height={20}
                color={ssnConfirmType === 'approve' ? '#15803d' : '#dc2626'}
                sx={{ flexShrink: 0, mt: 0.15 }}
              />
              <Typography variant="body2" color={ssnConfirmType === 'approve' ? '#14532d' : '#7f1d1d'} sx={{ lineHeight: 1.6 }}>
                {ssnConfirmType === 'approve'
                  ? 'Are you sure you want to approve this SSN update request? The user\'s SSN will be permanently changed. This action cannot be undone.'
                  : 'Are you sure you want to reject this SSN update request? This action cannot be undone.'}
              </Typography>
            </Stack>
            {ssnConfirmType === 'reject' && (
              <>
                <Stack direction="row" alignItems="center" spacing={0.8} mb={1}>
                  <Box sx={{ p: 0.45, bgcolor: 'error.lighter', borderRadius: 0.8, display: 'flex' }}>
                    <Iconify icon="eva:edit-2-outline" width={13} height={13} color="error.dark" />
                  </Box>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                    Rejection Reason <Typography component="span" variant="caption" color="error.main" fontWeight={700}>*</Typography>
                  </Typography>
                </Stack>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={ssnRejectReason}
                  onChange={(e) => setSsnRejectReason(e.target.value)}
                  placeholder="E.g. SSN does not match provided identity documents…"
                  disabled={ssnActionLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.8,
                      fontSize: 14,
                      '& fieldset': { borderWidth: 1.5, borderColor: 'grey.300' },
                      '&:hover fieldset': { borderColor: 'error.main' },
                      '&.Mui-focused fieldset': { borderColor: 'error.main', borderWidth: 2, boxShadow: '0 0 0 3px rgba(211,47,47,0.08)' },
                    },
                  }}
                />
              </>
            )}
          </Box>

          {/* Confirm Modal Footer */}
          <Box sx={{
            px: 3, py: 2,
            borderTop: '1.5px solid', borderColor: 'divider',
            background: ssnConfirmType === 'approve' ? 'linear-gradient(90deg, #f0fdf4, #f8fafc)' : 'linear-gradient(90deg, #fff5f5, #f8fafc)',
          }}>
            <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
              <Button
                variant="outlined"
                onClick={() => { setSsnConfirmOpen(false); setSsnRejectReason(''); }}
                disabled={ssnActionLoading}
                startIcon={<Iconify icon="eva:arrow-back-outline" width={16} height={16} />}
                sx={{ borderRadius: 2, borderWidth: 1.5, fontWeight: 700, '&:hover': { borderWidth: 1.5, bgcolor: 'grey.100' } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color={ssnConfirmType === 'approve' ? 'success' : 'error'}
                onClick={handleSsnConfirm}
                disabled={ssnActionLoading || (ssnConfirmType === 'reject' && !ssnRejectReason.trim())}
                startIcon={!ssnActionLoading && (
                  <Iconify
                    icon={ssnConfirmType === 'approve' ? 'eva:checkmark-circle-2-fill' : 'eva:close-circle-outline'}
                    width={18} height={18}
                  />
                )}
                sx={{
                  borderRadius: 2, px: 3.5, fontWeight: 700,
                  background: ssnConfirmType === 'approve'
                    ? 'linear-gradient(135deg, #2e7d32, #43a047)'
                    : 'linear-gradient(135deg, #c62828, #e53935)',
                  boxShadow: ssnConfirmType === 'approve'
                    ? '0 3px 12px rgba(46,125,50,0.32)'
                    : '0 3px 12px rgba(211,47,47,0.32)',
                  '&:hover:not(:disabled)': {
                    transform: 'translateY(-1px)',
                    boxShadow: ssnConfirmType === 'approve'
                      ? '0 6px 20px rgba(46,125,50,0.42)'
                      : '0 6px 20px rgba(211,47,47,0.42)',
                  },
                  '&:disabled': { opacity: 0.55 },
                  transition: 'all 0.2s',
                }}
              >
                {ssnActionLoading ? <CircularProgress size={18} color="inherit" /> : ssnConfirmType === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>
    </Page>
  );
}
